/**
 * Contextual shimmer skeletons for the Account hub.
 * Pure layout placeholders — no user-facing copy (i18n ratchet).
 */
import React from 'react';
import { Skeleton, SkeletonAvatar } from '../../components/ui/Skeleton';
import { flatCardStyles, cardStyles } from './styles';

function CardShell({
  children,
  flat = false,
}: {
  children: React.ReactNode;
  flat?: boolean;
}): JSX.Element {
  return (
    <div style={flat ? flatCardStyles : cardStyles} aria-hidden>
      {children}
    </div>
  );
}

/** Profile Information card shimmer */
export function ProfileCardSkeleton(): JSX.Element {
  return (
    <CardShell flat>
      <Skeleton className="mb-6 h-6 w-40" />
      <div className="mb-6 flex items-center gap-4">
        <SkeletonAvatar size="lg" className="h-16 w-16" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-8 w-32 rounded-full" />
          <Skeleton className="h-3 w-48" />
        </div>
      </div>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Skeleton className="h-4 w-20" />
          <Skeleton className="h-10 w-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
      <Skeleton className="h-10 w-36 rounded-full" />
    </CardShell>
  );
}

/** Security / notifications card shimmer */
export function SettingsCardSkeleton({ rows = 3 }: { rows?: number }): JSX.Element {
  return (
    <CardShell>
      <Skeleton className="mb-4 h-6 w-44" />
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, i) => (
          <div
            key={i}
            className="flex items-center justify-between gap-4 rounded-md border border-gray-200 p-4"
          >
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-3 w-56 max-w-full" />
            </div>
            <Skeleton className="h-6 w-11 shrink-0 rounded-full" />
          </div>
        ))}
      </div>
    </CardShell>
  );
}

/** Password form shimmer */
export function PasswordFormSkeleton(): JSX.Element {
  return (
    <CardShell>
      <Skeleton className="mb-4 h-6 w-40" />
      <div className="mb-6 space-y-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-10 w-full" />
          </div>
        ))}
      </div>
      <Skeleton className="h-10 w-40 rounded-full" />
    </CardShell>
  );
}

/** Full Profile tab shimmer — mirrors SettingsCard stack (titles inside cards). */
export function ProfileTabSkeleton(): JSX.Element {
  return (
    <div role="status" aria-busy="true">
      <CardShell>
        <div className="mb-4 flex items-start gap-3">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-32" />
            <Skeleton className="h-3 w-64 max-w-full" />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-2 rounded-lg border border-gray-200 p-4">
              <Skeleton className="h-4 w-4" />
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-3 w-full" />
            </div>
          ))}
        </div>
      </CardShell>
      <CardShell>
        <div className="mb-4 flex items-start gap-3">
          <Skeleton className="h-9 w-9 rounded-lg" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-5 w-28" />
            <Skeleton className="h-3 w-56 max-w-full" />
          </div>
        </div>
        <Skeleton className="h-11 w-full max-w-md" />
      </CardShell>
      <ProfileCardSkeleton />
      <PasswordFormSkeleton />
      <SettingsCardSkeleton rows={1} />
      <SettingsCardSkeleton rows={2} />
      <SettingsCardSkeleton rows={1} />
    </div>
  );
}

/** Billing tab shimmer (fits Account hub — not the full /settings/billing page) */
export function AccountBillingSkeleton(): JSX.Element {
  return (
    <div className="space-y-6" role="status" aria-busy="true">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {Array.from({ length: 2 }).map((_, i) => (
          <div key={i} className="rounded-lg border border-gray-200 bg-white p-5 space-y-4">
            <div className="flex items-center justify-between">
              <Skeleton className="h-5 w-28" />
              <Skeleton className="h-6 w-16 rounded-full" />
            </div>
            <Skeleton className="h-9 w-24" />
            <Skeleton className="h-3 w-full rounded-full" />
            <Skeleton className="h-4 w-40" />
          </div>
        ))}
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-5">
        <Skeleton className="mb-6 h-6 w-36" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="space-y-4 rounded-xl border border-gray-200 p-5">
              <Skeleton className="mx-auto h-5 w-20" />
              <Skeleton className="mx-auto h-9 w-16" />
              <div className="space-y-2 pt-2">
                {Array.from({ length: 4 }).map((_, j) => (
                  <Skeleton key={j} className="h-3 w-full" />
                ))}
              </div>
              <Skeleton className="h-10 w-full rounded-full" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function FieldPairSkeleton(): JSX.Element {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <div className="space-y-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-10 w-full" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-10 w-full" />
      </div>
    </div>
  );
}

/** GeneralTab shimmer — Your Business, basics, logo, contact */
export function BrandGeneralSkeleton(): JSX.Element {
  return (
    <div role="status" aria-busy="true">
      <CardShell flat>
        <Skeleton className="mb-4 h-6 w-32" />
        <FieldPairSkeleton />
      </CardShell>
      <CardShell flat>
        <Skeleton className="mb-4 h-6 w-28" />
        <div className="mb-4 space-y-4">
          <FieldPairSkeleton />
          <FieldPairSkeleton />
        </div>
        <Skeleton className="h-10 w-36 rounded-full" />
      </CardShell>
      <CardShell flat>
        <Skeleton className="mb-4 h-6 w-24" />
        <div className="mb-4 flex items-center gap-4">
          <SkeletonAvatar size="lg" className="h-16 w-16" />
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-10 w-40 rounded-full" />
            <Skeleton className="h-3 w-48 max-w-full" />
          </div>
        </div>
        <Skeleton className="h-10 w-36 rounded-full" />
      </CardShell>
      <CardShell flat>
        <Skeleton className="mb-4 h-6 w-40" />
        <div className="mb-4">
          <FieldPairSkeleton />
        </div>
        <Skeleton className="h-10 w-36 rounded-full" />
      </CardShell>
    </div>
  );
}

/** BrandIdentityTab shimmer — colors, typography, voice, preview */
export function BrandIdentitySkeleton(): JSX.Element {
  return (
    <div role="status" aria-busy="true">
      <CardShell flat>
        <Skeleton className="mb-6 h-6 w-32" />
        <div className="mb-6 space-y-3">
          <Skeleton className="h-4 w-28" />
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-md" />
            <Skeleton className="h-10 flex-1" />
          </div>
        </div>
        <div className="mb-6 space-y-3">
          <Skeleton className="h-4 w-32" />
          <div className="flex items-center gap-3">
            <Skeleton className="h-10 w-10 rounded-md" />
            <Skeleton className="h-10 flex-1" />
          </div>
        </div>
        <Skeleton className="h-10 w-36 rounded-full" />
      </CardShell>
      <CardShell flat>
        <Skeleton className="mb-4 h-6 w-28" />
        <div className="mb-4 space-y-2">
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-10 w-full" />
        </div>
        <Skeleton className="h-10 w-40 rounded-full" />
      </CardShell>
      <CardShell flat>
        <Skeleton className="mb-4 h-6 w-28" />
        <div className="mb-4 space-y-4">
          <div className="space-y-2">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
        <Skeleton className="h-10 w-36 rounded-full" />
      </CardShell>
      <CardShell flat>
        <Skeleton className="mb-4 h-6 w-24" />
        <Skeleton className="h-40 w-full rounded-lg" />
      </CardShell>
    </div>
  );
}

/** Team members list rows shimmer */
export function BrandTeamMembersSkeleton({ rows = 3 }: { rows?: number }): JSX.Element {
  return (
    <div className="space-y-3" role="status" aria-busy="true">
      {Array.from({ length: rows }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-between gap-4 rounded-md border border-gray-200 p-3"
        >
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <SkeletonAvatar size="md" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-3 w-44 max-w-full" />
            </div>
          </div>
          <Skeleton className="h-9 w-24 rounded-md" />
        </div>
      ))}
    </div>
  );
}

/** Full Brand DNA tab shimmer — mirrors every card stack on ?tab=brand */
export function BrandTabSkeleton(): JSX.Element {
  return (
    <>
      <BrandGeneralSkeleton />
      <BrandIdentitySkeleton />
      <CardShell flat>
        <Skeleton className="mb-4 h-6 w-28" />
        <FieldPairSkeleton />
        <Skeleton className="mt-4 h-10 w-28 rounded-full" />
      </CardShell>
      <CardShell flat>
        <Skeleton className="mb-4 h-6 w-36" />
        <BrandTeamMembersSkeleton />
      </CardShell>
      <SettingsCardSkeleton rows={4} />
      <CardShell flat>
        <Skeleton className="mb-3 h-6 w-36" />
        <Skeleton className="mb-3 h-3 w-64 max-w-full" />
        <Skeleton className="mb-3 h-24 w-full" />
        <Skeleton className="h-10 w-36 rounded-full" />
      </CardShell>
    </>
  );
}

/** Support tab form shimmer */
export function SupportTabSkeleton(): JSX.Element {
  return (
    <div className="space-y-10" role="status" aria-busy="true">
      <CardShell flat>
        <div className="flex items-center gap-3">
          <Skeleton className="h-2.5 w-2.5 rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-3 w-64 max-w-full" />
          </div>
        </div>
      </CardShell>
      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <CardShell flat>
            <Skeleton className="mb-4 h-6 w-40" />
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-10 w-full" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-10 w-full" />
              </div>
            </div>
            <div className="mb-4 space-y-2">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="mb-4 space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-28 w-full" />
            </div>
            <Skeleton className="ml-auto h-10 w-36 rounded-full" />
          </CardShell>
        </div>
        <CardShell flat>
          <Skeleton className="mb-4 h-6 w-44" />
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </CardShell>
      </div>
    </div>
  );
}

/** Compact sessions list shimmer */
export function SessionsSkeleton(): JSX.Element {
  return (
    <div className="space-y-3" role="status" aria-busy="true">
      {Array.from({ length: 2 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-between gap-4 rounded-md border border-gray-200 p-4"
        >
          <div className="min-w-0 flex-1 space-y-2">
            <Skeleton className="h-4 w-44" />
            <Skeleton className="h-3 w-52 max-w-full" />
          </div>
          <Skeleton className="h-8 w-20 rounded-full" />
        </div>
      ))}
    </div>
  );
}

/** Full-page integration detail shimmer (shopify, ads, CRM, etc.) */
export function IntegrationPageSkeleton(): JSX.Element {
  return (
    <div className="w-full space-y-6 p-4 sm:p-6" role="status" aria-busy="true">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-lg" />
        <div className="space-y-2">
          <Skeleton className="h-6 w-40" />
          <Skeleton className="h-4 w-56" />
        </div>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-5 space-y-4">
        <Skeleton className="h-5 w-32" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-[75%]" />
        <div className="flex flex-wrap gap-2 pt-2">
          <Skeleton className="h-10 w-28 rounded-full" />
          <Skeleton className="h-10 w-24 rounded-full" />
        </div>
      </div>
      <div className="rounded-lg border border-gray-200 bg-white p-5 space-y-3">
        <Skeleton className="h-5 w-36" />
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    </div>
  );
}
